// ===== Offers Manager Page (Backend Integrated) =====
import React, { useState } from 'react';
import VenueDashboardHeader from '../components/VenueDashboardHeader';
import { useVenueDashboard } from '../contexts/VenueDashboardContext';
import { PlusIcon, PencilIcon, TrashIcon, GiftIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

const OffersManagerPage = () => {
  const { canAccess, offers, loading, venueId, refreshData } = useVenueDashboard();
  const [isCreating, setIsCreating] = useState(false);

  if (!canAccess(['MANAGER', 'OWNER', 'ADMIN'])) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
        <VenueDashboardHeader title="Offers Manager" />
        <div style={{ padding: '40px 20px', textAlign: 'center' }}>
          <p style={{ color: '#ef4444' }}>Access denied. Manager role or higher required.</p>
        </div>
      </div>
    );
  }

  // Calculate stats from real data
  const activeOffers = offers?.filter(o => o.status === 'active' || o.isActive) || [];
  const totalRedemptions = offers?.reduce((sum, o) => sum + (o.redemptionCount || o.redemptions || 0), 0) || 0;
  const scheduledOffers = offers?.filter(o => o.status === 'scheduled' || o.isScheduled) || [];

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      <VenueDashboardHeader title="Offers Manager" />
      
      <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
        {/* Header Actions */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px'
        }}>
          <h2 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
            Your Offers
          </h2>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              onClick={refreshData}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                backgroundColor: 'white',
                color: '#6b7280',
                fontWeight: '500',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <ArrowPathIcon style={{ width: '16px', height: '16px' }} />
              Refresh
            </button>
            <button
              onClick={() => setIsCreating(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 16px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#3b82f6',
                color: 'white',
                fontWeight: '600',
                fontSize: '14px',
                cursor: 'pointer'
              }}
              data-testid="create-offer-button"
            >
              <PlusIcon style={{ width: '18px', height: '18px' }} />
              Create Offer
            </button>
          </div>
        </div>

        {/* Stats */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '12px',
          marginBottom: '24px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>Active Offers</p>
            <p style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', margin: '4px 0 0 0' }}>
              {activeOffers.length}
            </p>
          </div>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>Total Redemptions</p>
            <p style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', margin: '4px 0 0 0' }}>
              {totalRedemptions}
            </p>
          </div>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '16px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <p style={{ fontSize: '12px', color: '#6b7280', margin: 0 }}>Scheduled</p>
            <p style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', margin: '4px 0 0 0' }}>
              {scheduledOffers.length}
            </p>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <ArrowPathIcon style={{ 
              width: '32px', 
              height: '32px', 
              color: '#3b82f6',
              animation: 'spin 1s linear infinite'
            }} />
            <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
          </div>
        )}

        {/* Empty State */}
        {!loading && (!offers || offers.length === 0) && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '40px',
            textAlign: 'center',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <GiftIcon style={{ width: '48px', height: '48px', color: '#9ca3af', margin: '0 auto 16px' }} />
            <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: '0 0 8px 0' }}>
              No Offers Yet
            </h3>
            <p style={{ fontSize: '14px', color: '#6b7280', margin: '0 0 20px 0' }}>
              Create your first offer to attract customers
            </p>
            <button
              onClick={() => setIsCreating(true)}
              style={{
                padding: '12px 24px',
                borderRadius: '8px',
                border: 'none',
                backgroundColor: '#3b82f6',
                color: 'white',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Create Offer
            </button>
          </div>
        )}

        {/* Offers List */}
        {!loading && offers && offers.length > 0 && (
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            {offers.map((offer, index) => (
              <div
                key={offer._id || offer.id || index}
                style={{
                  padding: '16px 20px',
                  borderBottom: index < offers.length - 1 ? '1px solid #e5e7eb' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '16px'
                }}
              >
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: '#dbeafe',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <GiftIcon style={{ width: '24px', height: '24px', color: '#3b82f6' }} />
                </div>
                
                <div style={{ flex: 1 }}>
                  <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                    {offer.title || offer.name || 'Untitled Offer'}
                  </h3>
                  <p style={{ fontSize: '13px', color: '#6b7280', margin: '4px 0 0 0' }}>
                    {offer.offerType || offer.type || 'Offer'} • {offer.redemptionCount || offer.redemptions || 0} redemptions
                    {offer.expiresAt && ` • Expires ${new Date(offer.expiresAt).toLocaleDateString()}`}
                  </p>
                </div>
                
                <span style={{
                  padding: '4px 10px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '500',
                  backgroundColor: (offer.status === 'active' || offer.isActive) ? '#dcfce7' : '#fef9c3',
                  color: (offer.status === 'active' || offer.isActive) ? '#166534' : '#854d0e'
                }}>
                  {offer.status || (offer.isActive ? 'Active' : 'Inactive')}
                </span>
                
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    style={{
                      padding: '8px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <PencilIcon style={{ width: '16px', height: '16px', color: '#6b7280' }} />
                  </button>
                  <button
                    style={{
                      padding: '8px',
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb',
                      backgroundColor: 'white',
                      cursor: 'pointer'
                    }}
                  >
                    <TrashIcon style={{ width: '16px', height: '16px', color: '#ef4444' }} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default OffersManagerPage;
